import joi from 'joi';
import React, { KeyboardEvent, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Theme,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfNumberField, useAlerts, useQlfTranslation } from '@qualifio/ui-commons';
import { Trans, useTranslation } from 'react-i18next';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { makeStyles } from '@mui/styles';
import { formatNumber } from '../../../shared/utils';
import { useJoiResolver } from '../../../shared/forms/use-joi-resolver';
import { useProgram } from '../../../loyalty/ProgramContext';

const signValues = [
  { value: 1, text: '+' },
  { value: -1, text: '-' },
];

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  dialog: {
    minWidth: 500,
  },
  dialogTitle: {
    padding: spacing(4, 8, 1),
    textAlign: 'center',
  },
  dialogContent: {
    paddingTop: spacing(2),
    paddingBottom: spacing(1),
  },
  dialogActions: {
    padding: spacing(1, 8, 4),
    justifyContent: 'center',
  },
  amountSign: {
    fontWeight: 700,
  },
  currencyIcon: {
    marginLeft: spacing(0.5),
  },
  result: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'end',
    padding: spacing(1),
  },
  resultAmount: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'end',
    width: 105,
    paddingRight: 4,
    paddingLeft: spacing(0.5),
    fontWeight: 700,
  },
  commentLabel: {
    marginBottom: spacing(1),
  },
}));

export interface AdjustParticipationDialogProps {
  rewardNumberParticipation: number;
  rewardNumberUsed: number;
  onClose: () => void;
  onApply?: (newVal: number) => Promise<void> | void;
}

interface FormValue {
  sign: number;
  rawAmount: string;
}

const adjustBalanceFormSchema = joi.object<FormValue>({
  sign: joi.number().valid(-1, 1),
  rawAmount: joi.number().positive().integer().max(1000000),
});

export function AdjustParticipationDialog({
  rewardNumberParticipation,
  rewardNumberUsed,
  onClose,
  onApply,
}: AdjustParticipationDialogProps) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { notify } = useAlerts();
  const [applying, setApplying] = useState(false);
  const { program } = useProgram();

  const resolver = useJoiResolver(adjustBalanceFormSchema);
  const {
    watch,
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValue>({
    defaultValues: {
      sign: 1,
      rawAmount: '',
    },
    resolver,
  });

  const sign = watch('sign');
  const rawAmount = watch('rawAmount');
  const amount = useMemo(() => sign * (parseInt(rawAmount) || 0), [sign, rawAmount]);
  const { t: htmlT } = useQlfTranslation('translation');

  const onAmountKeyDown = useCallback(
    (event: KeyboardEvent<unknown>) => {
      if (event.key === '-') {
        setValue('sign', -1);
      }
      if (event.key === '+') {
        setValue('sign', 1);
      }
    },
    [setValue],
  );

  const handleApply = useCallback(async () => {
    setApplying(true);
    try {
      const sign = getValues('sign');
      const rawAmount = parseInt(getValues('rawAmount'), 10) || 0;
      const newVal: number = rewardNumberParticipation + sign * rawAmount;
      await onApply?.(newVal);
      onClose();
    } catch (error) {
      console.error(error);
      notify(t('profiles.currency-balances.adjust.apply.error', { error }), 'error');
    } finally {
      setApplying(false);
    }
  }, [program, amount]);

  const projectedNewVal = rewardNumberParticipation - rewardNumberUsed;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <IconButton onClick={onClose} sx={{ position: 'absolute', right: 0 }}>
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogTitle className={classes.dialogTitle}>
        {t('reward.campaign.adjust.participation.value.title')}
      </DialogTitle>
      <form onSubmit={handleSubmit(handleApply, console.error)}>
        <DialogContent className={classes.dialogContent}>
          <Box
            sx={{
              margin: '0 auto',
              width: 'fit-content',
            }}
          >
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      paddingLeft: 0.5,
                      fontWeight: 700,
                    }}
                  >
                    {formatNumber(rewardNumberParticipation || 0, i18n.language)}
                  </Box>
                  <Controller
                    control={control}
                    name="sign"
                    render={({ field: { ref, ...field } }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        select
                        SelectProps={{
                          classes: {
                            select: classes.amountSign,
                          },
                        }}
                        fullWidth={false}
                        sx={{ marginLeft: 1 }}
                      >
                        {signValues.map(val => (
                          <MenuItem key={val.value} value={val.value}>
                            {val.text}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>
                <Controller
                  control={control}
                  name="rawAmount"
                  render={({ field: { ref, ...field } }) => (
                    <QlfNumberField
                      {...field}
                      inputRef={ref}
                      sx={{
                        marginLeft: 1,
                        marginBottom: 0,
                        width: 105,
                        '& .MuiInputBase-input': {
                          textAlign: 'right',
                        },
                      }}
                      fullWidth={false}
                      onKeyDown={onAmountKeyDown}
                      autoComplete="off"
                      error={!!errors.rawAmount}
                      helperText={errors.rawAmount?.message}
                    />
                  )}
                />
              </Box>
              <div className={classes.result}>
                {t('profiles.currency-balances.adjust.result.label')}
                <div className={classes.resultAmount}>
                  {formatNumber(rewardNumberParticipation + amount, i18n.language)}
                </div>
              </div>
            </Box>
          </Box>
          <>
            {rewardNumberParticipation + amount < rewardNumberUsed &&
            rewardNumberParticipation + amount >= 0 ? (
              <Box sx={{ color: '#CB2028' }}>
                <Trans
                  t={t}
                  i18nKey="reward.campaign.adjust.error.message.used"
                  values={{ projectedNewVal: projectedNewVal || 'projectedNewVal' }}
                  components={{ b: <strong /> }}
                />
              </Box>
            ) : rewardNumberParticipation + amount < 0 ? (
              <Box sx={{ color: '#CB2028' }}>
                {t('reward.campaign.adjust.error.message.negative')}
              </Box>
            ) : (
              <></>
            )}
          </>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button color="grey" onClick={onClose}>
            {t('generic.actions.cancel')}
          </Button>
          <Button
            color="success"
            type="submit"
            startIcon={applying && <FontAwesomeIcon icon={faSpinner} spin />}
            disabled={applying || rewardNumberParticipation + amount < rewardNumberUsed}
            autoFocus
          >
            {t('profiles.currency-balances.adjust.apply.label')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
